/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import { Settings } from '@/data/Settings';
import { User } from '@/data/User';
import { DEFAULT_LANGUAGE } from '@/data/config/DEFAULTS';
import { LANGUAGE_MAP } from '@/data/constants/environment';
import {
	dataRouteManifest,
	dataRouteProtection,
	dataRouteSessionScoped,
} from '@/data/containers/manifest';
import { Order } from '@/data/types/Order';
import { PageDataFromId } from '@/data/types/PageDataFromId';
import { TranslationTable, requestTranslation } from 'integration/generated/translations';
import { ParsedUrlQuery, stringify } from 'querystring';

type PageDataLookupProps = {
	pub: boolean;
	path: ParsedUrlQuery['path'];
	localeId: string;
	user: Partial<User>;
	cart?: Order | boolean; // boolean represent having cart or not
	settings?: Settings;
};

type ProtectedRouteGetter = {
	translations: Awaited<ReturnType<typeof requestTranslation>>;
	user: Partial<User>;
	translateKey?: string;
	cart?: Order | boolean;
	settings?: Settings;
};

type LocalRoutes = TranslationTable['Routes'];
const TAUTOLOGY = () => ({
	allowed: true,
	redirectToRoute: undefined,
	redirectToUrl: undefined,
	query: undefined,
});
const SESSION_ERROR = {
	allowed: false,
	redirectToRoute: undefined,
	redirectToUrl: '/iniciar-sesion',
	query: undefined,
};

// Handle server side session error here, redirect to session error page and clear cookie if there is session error.
const resolveRedirect = (
	user: Partial<User>,
	translateKey: keyof LocalRoutes,
	cart?: Order | boolean,
	settings?: Settings
) => {
	// Solo redirigir a SESSION_ERROR si hay un sessionError Y la ruta depende de la sesión
	const isProtectedRoute = translateKey && dataRouteProtection[translateKey];
	const needsSession = isProtectedRoute || (translateKey && dataRouteSessionScoped[translateKey]);

	if (user?.sessionError && needsSession) {
		return SESSION_ERROR;
	}

	return (isProtectedRoute || TAUTOLOGY)(user, cart, settings);
};
/**
 * Returns the redirect information from route protection if it exists.
 */
const getProtectedRouteKey = ({
	translations,
	user,
	translateKey,
	cart,
	settings,
}: ProtectedRouteGetter): {
	translateKey?: string;
	redirect?: string;
	query?: Record<string, any>;
} => {
	const hasCart = typeof cart === 'boolean' ? cart : !!cart?.orderItem;
	const { allowed, redirectToRoute, redirectToUrl, query } = resolveRedirect(
		user,
		translateKey as keyof LocalRoutes,
		hasCart,
		settings
	);
	const redirectDefinition =
		!allowed &&
		redirectToRoute &&
		translations.hasOwnProperty(redirectToRoute) &&
		translations[redirectToRoute];

	const redirect =
		typeof redirectDefinition === 'object' ? `/${redirectDefinition.route}` : redirectToUrl;

	return {
		translateKey: allowed ? translateKey : undefined,
		redirect: redirect?.toString() || undefined,
		query: query || undefined,
	};
};

/**
 * Loads the Routes translations for the passed in localeId and looks
 * for a route match in the rout manifest. If found, it returns a PageData
 * object.
 */
export const getStaticRoutePageData = async ({
	pub,
	path,
	localeId,
	user,
	cart,
	settings,
}: PageDataLookupProps): Promise<PageDataFromId | string | undefined> => {
	const locale = Object.entries(LANGUAGE_MAP)
		.find(([key]) => key === localeId)
		?.at(-1);
	// TODO Pass translations in, use caching with getLocalization
	const translations = await requestTranslation({
		locale: locale || LANGUAGE_MAP[DEFAULT_LANGUAGE],
		section: 'Routes',
	});
	const [idReverseTranslate, translationsFromRoutId] =
		Object.entries(translations).find(
			([_, value]) => typeof value === 'object' && path && value?.route === [...path].join('/')
		) || [];
	let redirect;
	let translateKey = idReverseTranslate;
	let query;
	if (!pub && translateKey !== 'SessionError') {
		// not to redirect from session error to session error
		// redirect only need to be handled server-side for response status code 3xx.
		const protectedRouteKey = getProtectedRouteKey({
			translations,
			user,
			translateKey: idReverseTranslate,
			cart,
			settings,
		});
		redirect = protectedRouteKey.redirect;
		translateKey = protectedRouteKey.translateKey;
		query = protectedRouteKey.query;
	}
	const routeKey =
		translateKey &&
		Object.entries(dataRouteManifest)
			.find(([key]) => key === translateKey)
			?.at(-1);

	const {
		title = '',
		description = '',
		keywords = '',
	} = routeKey && typeof translationsFromRoutId === 'object' ? translationsFromRoutId : {};
	if (redirect) {
		const hasQuery = query && Object.keys(query).length > 0;
		const queryString = hasQuery ? `${redirect.includes('?') ? '&' : '?'}${stringify(query)}` : '';
		return `${redirect}${queryString}`;
	}

	if (routeKey) {
		return {
			layout: {
				name: routeKey.toString(),
				containerName: routeKey.toString(),
				isStoreDefault: true,
			},
			page: {
				type: routeKey.toString(),
				title: title.toString(),
				metaDescription: description.toString(),
				metaKeyword: keywords.toString(),
			},
			identifier: '',
			tokenExternalValue: `${routeKey}`,
			tokenName: '',
			tokenValue: '',
		};
	}

	return undefined;
};
